import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    AddAccountParentRelationship as AddAccountParentRelationshipMutation,
    AddAccountParentRelationshipVariables as AddAccountParentRelationshipMutationVariables,
    AccountParentSetInput,
} from 'nutshell-graphql-types';

import ACCOUNT_HIERARCHY_INFO_FRAGMENT from '../fragments/account-hierarchy-info-fragment.graphql';
import ADD_ACCOUNT_PARENT_RELATIONSHIP from '../mutations/add-account-parent-relationship.graphql';

export const useAddAccountParentRelationship = (
    currentAccountId: string
): {
    addAccountParentRelationship: (input: AccountParentSetInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [addAccountParentRelationship, {loading, error}] = useMutation<
        AddAccountParentRelationshipMutation,
        AddAccountParentRelationshipMutationVariables
    >(ADD_ACCOUNT_PARENT_RELATIONSHIP);

    return {
        addAccountParentRelationship: (input: AccountParentSetInput) => {
            return addAccountParentRelationship({
                variables: {input},
                update: (cache, mutationResults) => {
                    const payload =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.accountParentSet;

                    if (!payload) {
                        return;
                    }

                    // Store the parent or child account being returned based on which one matches the currentAccountId
                    let accountToUpdate = null;
                    if (payload.account?.id === currentAccountId) {
                        accountToUpdate = payload.account;
                    } else if (payload.parentAccount?.id === currentAccountId) {
                        accountToUpdate = payload.parentAccount;
                    }

                    if (!accountToUpdate) {
                        return;
                    }

                    cache.writeFragment({
                        id: currentAccountId,
                        fragment: ACCOUNT_HIERARCHY_INFO_FRAGMENT,
                        fragmentName: 'AccountHierarchyInfoFragment',
                        data: accountToUpdate,
                    });
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
